import React, { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import {jwtDecode} from 'jwt-decode'; // Corrected import
import UserContext from './userContext';
import analytics, { AnalyticsEvents } from '../services/analytics';

/**
 * Fetch user's household and set up group analytics
 */
const setupHouseholdAnalytics = async (userId) => {
  try {
    const { data: householdUser, error } = await supabase
      .from("households_users")
      .select("household_id")
      .eq("user_id", userId)
      .single();

    if (error) {
      // User might not be part of a household - that's OK
      if (error.code !== 'PGRST116') {
        console.debug('[Analytics] No household found for user');
      }
      return null;
    }

    if (householdUser?.household_id) {
      // Set up household group in analytics
      analytics.group('household', householdUser.household_id);
      return householdUser.household_id;
    }
  } catch (err) {
    console.error('[Analytics] Error fetching household:', err);
  }
  return null;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // Start with undefined to indicate loading
  const [session, setSession] = useState(undefined); // Start with undefined

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Error fetching session:', error);
        setUser(null);
        setSession(null);
        return;
      }

      if (session?.user && session?.access_token) {
        // Decode the JWT to get user_role
        const decodedToken = jwtDecode(session.access_token);
        const userRole = decodedToken.user_role || 'user';
        const isAdmin = userRole === 'admin';
        setUser({ ...session.user, user_role: userRole });
        setSession(session);

        // Identify user in analytics on app load
        analytics.identify(session.user.id, {
          email: session.user.email,
          role: userRole,
          provider: session.user.app_metadata?.provider,
        });

        // Update session recording based on admin status
        analytics.updateSessionRecording(isAdmin);

        // Set up household group analytics (async, don't block)
        setupHouseholdAnalytics(session.user.id);
      } else {
        setUser(null); // Explicitly set to null if not authenticated
        setSession(null);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user && session?.access_token) {
        const decodedToken = jwtDecode(session.access_token);
        const userRole = decodedToken.user_role || 'user';
        const isAdmin = userRole === 'admin';
        const enrichedUser = { ...session.user, user_role: userRole };
        setUser(enrichedUser);
        setSession(session);

        // Identify user in analytics
        analytics.identify(session.user.id, {
          email: session.user.email,
          role: userRole,
          provider: session.user.app_metadata?.provider,
        });

        // Update session recording based on admin status
        analytics.updateSessionRecording(isAdmin);

        // Track sign in event
        if (event === 'SIGNED_IN') {
          analytics.track(AnalyticsEvents.USER_SIGNED_IN, {
            provider: session.user.app_metadata?.provider,
          });

          // Set up household group analytics on sign in
          setupHouseholdAnalytics(session.user.id);
        }
      } else {
        setUser(null);
        setSession(null);

        // Reset analytics on sign out
        if (event === 'SIGNED_OUT') {
          analytics.track(AnalyticsEvents.USER_SIGNED_OUT);
          analytics.reset();
        }
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  return (
    <UserContext.Provider value={{ user, session }}>
      {children}
    </UserContext.Provider>
  );
};

export default AuthProvider;